import moment from 'moment-timezone';
import { get } from 'lodash';
import { EChartOption } from 'echarts/lib/echarts';
import { humanize } from '@utils';
import { UNITS, TIMELINE_DIMENSION, ALL_DISPLAY_NAMES } from '@components/session-timeline/utils/constants';
import {
  convertStateDataToChart,
  renderStateItem,
} from '../../../utils/converDataToChart';
import formatBPS from '../../../utils/formatBPS';
import getSessionDetailsByTime from '../../../utils/getSessionDetailsByTime';

export default ({
  stateData,
  dateRange,
  timelineData,
  granularity,
}): EChartOption => {
  const stateChartData = convertStateDataToChart(stateData);
  const min = dateRange[0];
  const max = dateRange[dateRange.length - 1];
  const diff = moment(max).diff(min, granularity);
  const timeFormat = granularity === 'm' ? 'HH:mm' : 'HH:mm:ss';
  const option: EChartOption = {
    tooltip: {
      show: true,
      trigger: 'axis',
      axisPointer: {
        type: 'line',
        show: true,
        axis: 'x',
        snap: false,
        label: {
          show: false,
        },
        lineStyle: {
          color: '#0046ff',
        },
      },
      formatter: (params) => {
        const axisValue = get(params, [0, 'axisValue'], 0);
        const details = getSessionDetailsByTime(timelineData, axisValue, granularity);
        const stateValue = get(details, TIMELINE_DIMENSION.SessionState, '');
        const [names, values] = Object.keys(details).reduce(([resultNames, resultValues], eventName) => {
          if (eventName === TIMELINE_DIMENSION.SessionState) {
            return [resultNames, resultValues];
          }
          const eventNameDisplayName = ALL_DISPLAY_NAMES[eventName] || eventName;
          const eventValueDisplayName = ALL_DISPLAY_NAMES[details[eventName]] || details[eventName];
          let unit = UNITS[eventName];
          let value = eventValueDisplayName;
          if (unit === 'bps') {
            // The unit of the value returned by backend is k, so it need to * 1000.
            const [bpsValue, bpsUnit] = formatBPS(value, eventName);
            value = bpsValue;
            unit = bpsUnit;
          } else if (unit) {
            value = humanize(eventValueDisplayName, '');
          }
          const newResultNames = `
            ${resultNames}
            <li>${eventNameDisplayName}</li>
          `;
          const newResultValues = `
            ${resultValues}
            <li>${value} ${unit || ''}</li>
          `;
          return [newResultNames, newResultValues];
        }, [`<li>${ALL_DISPLAY_NAMES[TIMELINE_DIMENSION.SessionState]}</li>`, `<li>${ALL_DISPLAY_NAMES[stateValue] || stateValue}</li>`]);
        return `
          <div class="echarts-tooltip">
            <div class="time">
            ${moment(axisValue).format('MMM DD, YYYY HH:mm:ss')}
            </div>
            <div class="details">
              <ul class="item_names">${names}</ul>
              <ul class="item_values">${values}</ul>
            </div>
          </div>
        `;
      },
    },
    grid: [{
      show: false,
      left: 30,
      right: 95,
      top: 0,
      bottom: 30,
    }],
    xAxis: [{
      type: 'time',
      axisLabel: {
        formatter: value => moment(value).format(timeFormat),
        hideOverlap: true,
      },
      min,
      max,
      data: dateRange,
      splitLine: {
        show: false,
      },
      splitNumber: Math.min(14, diff),
    }],
    yAxis: {
      show: false,
    },
    series: [
      {
        type: 'bar',
        large: true,
        data: dateRange,
        itemStyle: {
          opacity: 0,
        },
      },
      {
        type: 'custom',
        renderItem: renderStateItem,
        clip: true, // clip the redundant chart.
        encode: {
          x: [1, 2],
          y: 0,
        },
        data: stateChartData,
      },
    ],
  };
  return option;
};
